import { useCallback, useState } from 'react';
import axios from 'axios';

import useOrganization from '~/components/OrganizationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

import type { GbgConfigurationFormValues, GbgConfigurationResponse } from '../types';

interface UseGbgConfigurationReturnType {
  isLoading: boolean;
  isError: boolean;
  configuration: GbgConfigurationResponse['configuration'] | null;
  hasConfiguration: boolean;
  /** Calls reportAxiosError and rethrows on error */
  saveConfiguration: (values: GbgConfigurationFormValues) => Promise<void>;
  /** Calls reportAxiosError and rethrows on error */
  enableConfiguration: (shouldEnable?: boolean) => Promise<void>;
  reloadData: () => Promise<GbgConfigurationResponse | undefined>;
  isSaving: boolean;
}

export const useGbgConfiguration = (): UseGbgConfigurationReturnType => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error useSysconfig is not yet typed
  const { organization } = useSysconfig();
  // OrganizationContext is still JS (`createContext({})`), so the hook infers `{}`.
  const { reloadOperationalDetails } = useOrganization() as { reloadOperationalDetails: () => Promise<unknown> };
  const [isSaving, setIsSaving] = useState(false);
  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/gbg/configuration`;

  const { isLoading, isError, data, reloadData } = useDataFetcher<GbgConfigurationResponse>(baseRoute);

  const configuration = data?.configuration ?? null;
  // initial configuration saved with only the `is_enabled` set, so we're checking for the presence
  // of the API base URL, API username, and API token output to determine if the configuration is complete
  const hasConfiguration = Boolean(
    configuration?.id && configuration.api_base_url && configuration.api_username && configuration.api_token_output
  );

  const saveConfiguration = useCallback(
    async (values: GbgConfigurationFormValues) => {
      try {
        setIsSaving(true);
        await axios.put(baseRoute, values);
        // Address fields read the GBG validation status from the operational details.
        await Promise.all([reloadData(), reloadOperationalDetails()]);
      } catch (error) {
        await reportAxiosError(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [baseRoute, reloadData, reloadOperationalDetails]
  );

  const enableConfiguration = useCallback(
    async (shouldEnable: boolean = true) => {
      try {
        setIsSaving(true);
        await axios.put(baseRoute, { is_enabled: shouldEnable });
        // Address fields read the GBG validation status from the operational details.
        await Promise.all([reloadData(), reloadOperationalDetails()]);
      } catch (error) {
        await reportAxiosError(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [baseRoute, reloadData, reloadOperationalDetails]
  );

  return {
    isLoading,
    isError,
    configuration,
    hasConfiguration,
    saveConfiguration,
    enableConfiguration,
    reloadData,
    isSaving,
  };
};
